import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import AdminDomainCard from './components/AdminDomainCard';
import SecurityDashboard from './components/SecurityDashboard';
import SystemHealthMetrics from './components/SystemHealthMetrics';
import AdminActivityLog from './components/AdminActivityLog';

const MainAdminControlCenter = () => {
  const navigate = useNavigate();
  const { isAdmin, isAuthenticated, userProfile } = useAuth();
  const [systemHealth, setSystemHealth] = useState({
    status: 'operational',
    uptime: 99.99,
    activeUsers: 1247,
    activeSessions: 3,
    lastIncident: 'None in 30 days'
  });

  // Redirect non-admin users
  useEffect(() => {
    if (isAuthenticated && !isAdmin) {
      navigate('/user-wallet-dashboard');
    }
  }, [isAdmin, isAuthenticated, navigate]);

  // Access denied screen for non-admin users
  if (!isAdmin) {
    return (
      <MainLayout showControls={false}>
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50">
          <div className="text-center max-w-md mx-auto p-8">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="ShieldAlert" className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Access Denied</h1>
            <p className="text-gray-600 mb-6">
              This area is restricted to authorized administrators only. You do not have permission to access the Main Admin Control Center.
            </p>
            <button
              onClick={() => navigate('/user-wallet-dashboard')}
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  const adminDomains = [
    {
      id: 'operations',
      title: 'Operations Management',
      description: 'Real-time system monitoring, technical oversight, and infrastructure management',
      icon: 'Activity',
      path: '/real-time-operations',
      color: 'blue',
      metrics: [
        { label: 'System Uptime', value: '99.99%' },
        { label: 'Active Transactions', value: '1.2K/min' },
        { label: 'Response Time', value: '45ms' }
      ]
    },
    {
      id: 'users',
      title: 'User Administration',
      description: 'Account management, permissions control, and user behavior analytics',
      icon: 'Users',
      path: '/user-analytics-hub',
      color: 'purple',
      metrics: [
        { label: 'Total Users', value: '12,847' },
        { label: 'Active Today', value: '3,421' },
        { label: 'New Signups', value: '+127' }
      ]
    },
    {
      id: 'finance',
      title: 'Financial Controls',
      description: 'Revenue oversight, cost analytics, audit trails, and financial reporting',
      icon: 'DollarSign',
      path: '/financial-performance',
      color: 'green',
      metrics: [
        { label: 'Total Revenue', value: '$847K' },
        { label: 'Monthly Growth', value: '+12.4%' },
        { label: 'Profit Margin', value: '34.2%' }
      ]
    },
    {
      id: 'executive',
      title: 'Executive Analytics',
      description: 'Strategic insights, KPI tracking, compliance status, and executive reporting',
      icon: 'BarChart3',
      path: '/executive-overview',
      color: 'indigo',
      metrics: [
        { label: 'KPI Score', value: '94/100' },
        { label: 'Compliance', value: '100%' },
        { label: 'Regional Growth', value: '+8.7%' }
      ]
    },
    {
      id: 'payouts',
      title: 'Payout Administration',
      description: 'Settlement management, payout scheduling, compliance tracking, and reconciliation',
      icon: 'Banknote',
      path: '/pro-payout-management',
      color: 'amber',
      metrics: [
        { label: 'Pending Payouts', value: '$124K' },
        { label: 'Processed Today', value: '$89K' },
        { label: 'Success Rate', value: '99.8%' }
      ]
    }
  ];

  return (
    <MainLayout showControls={false}>
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/5">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-primary to-secondary text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <Icon name="Shield" className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h1 className="text-4xl font-bold">Main Admin Control Center</h1>
                    <p className="text-white/80 mt-1">Exclusive Administrative Hub</p>
                  </div>
                </div>
                <p className="text-white/90 max-w-2xl">
                  Comprehensive system oversight and access control management for authorized administrators.
                  All administrative actions are logged and monitored for security compliance.
                </p>
              </div>
              <div className="hidden lg:block">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="text-sm text-white/70 mb-1">Logged in as</div>
                  <div className="font-semibold text-lg">{userProfile?.full_name || userProfile?.email}</div>
                  <div className="text-xs text-white/60 mt-1">Main Administrator</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* System Health Metrics */}
          <SystemHealthMetrics systemHealth={systemHealth} />

          {/* Security Dashboard */}
          <SecurityDashboard />

          {/* Administrative Domains */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Administrative Domains</h2>
                <p className="text-gray-600 mt-1">Access core administrative functions and system controls</p>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Icon name="Lock" className="w-4 h-4" />
                <span>Role-Based Access Control Active</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {adminDomains?.map((domain) => (
                <AdminDomainCard key={domain?.id} domain={domain} />
              ))}
            </div>
          </div>

          {/* Admin Activity Log */}
          <AdminActivityLog />

          {/* Emergency Actions */}
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="AlertTriangle" className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Emergency Controls</h3>
                <p className="text-gray-600 mb-4">
                  Critical system controls for emergency situations. All actions require multi-factor authentication and are immediately logged.
                </p>
                <div className="flex flex-wrap gap-3">
                  <button className="px-4 py-2 bg-white border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium">
                    <Icon name="Power" className="w-4 h-4 inline mr-2" />
                    Maintenance Mode
                  </button>
                  <button className="px-4 py-2 bg-white border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium">
                    <Icon name="Bell" className="w-4 h-4 inline mr-2" />
                    System-Wide Alert
                  </button>
                  <button className="px-4 py-2 bg-white border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium">
                    <Icon name="ShieldOff" className="w-4 h-4 inline mr-2" />
                    Emergency Lockdown
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default MainAdminControlCenter;